import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp,
  TrendingDown,
  Remove,
  CheckCircle,
  Cancel,
  EventNote,
  Speed,
  CalendarToday,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../services/api';
import { Account, Event } from '../types';
import { useConfigStore } from '../stores/useConfigStore';
import { getOrderedEntityKeys } from '../utils/entityOrder';
import { ActivityHeatmap } from '../components/ActivityHeatmap';
import { FeatureAdoptionTimeline } from '../components/FeatureAdoptionTimeline';
import { StageProgressionTimeline } from '../components/StageProgressionTimeline';
import { ComparisonBenchmarks } from '../components/ComparisonBenchmarks';
import { RiskIndicators } from '../components/RiskIndicators';
import { KeyMilestones } from '../components/KeyMilestones';

/**
 * Account Detail Page
 * Comprehensive analytics for a single customer account
 */
export const AccountDetailPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { config, loadConfig } = useConfigStore();

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const [account, setAccount] = useState<Account | null>(null);
  const [healthTrend, setHealthTrend] = useState<any>(null);
  const [entityCohorts, setEntityCohorts] = useState<Record<string, any>>({});
  const [activityHeatmap, setActivityHeatmap] = useState<any>(null);
  const [stageHistory, setStageHistory] = useState<any>(null);
  const [benchmarks, setBenchmarks] = useState<any>(null);
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);

  // Loading states per section
  const [accountLoading, setAccountLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  // Error states per section
  const [accountError, setAccountError] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const featureDefinitions = useConfigStore((s) => s.config?.featureDefinitions);

  useEffect(() => {
    if (!accountId) return;

    // Guard against navigating between accounts faster than requests return:
    // every setState below is skipped once this effect has been superseded.
    let cancelled = false;

    const loadAccount = async () => {
      try {
        setAccountLoading(true);
        setAccountError(null);
        setAccount(null);
        setHealthTrend(null);
        setActivityHeatmap(null);
        setStageHistory(null);
        setBenchmarks(null);
        setRecentEvents([]);
        setEntityCohorts({});
        setSectionErrors({});

        // Load account details first (critical)
        const accountData = await api.getAccount(accountId);
        if (cancelled) return;
        setAccount(accountData);

        // Once account loads, load all sections in parallel
        loadSections(accountData);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Error loading account:', err);
        setAccountError(err.response?.data?.error || err.response?.data?.message || 'Account not found');
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    };

    const loadSections = async (accountData: Account) => {
      setSectionsLoading(true);
      const errors: Record<string, string> = {};

      // Load all sections in parallel with individual error handling
      const results = await Promise.allSettled([
        // Health trend
        api.getHealthTrend(accountId, 90).catch((err) => {
          console.warn('Health trend failed:', err);
          errors.healthTrend = 'Failed to load health trend';
          return null;
        }),

        // Activity heatmap
        api.getActivityHeatmap(accountId, 90).catch((err) => {
          console.warn('Activity heatmap failed:', err);
          errors.activityHeatmap = 'Failed to load activity heatmap';
          return null;
        }),

        // Stage history
        api.getStageHistory(accountId).catch((err) => {
          console.warn('Stage history failed:', err);
          errors.stageHistory = 'Failed to load stage history';
          return null;
        }),

        // Benchmarks
        api.getBenchmarks(accountId).catch((err) => {
          console.warn('Benchmarks failed:', err);
          errors.benchmarks = 'Failed to load benchmarks';
          return null;
        }),

        // Recent events
        api.listEvents({ accountId, limit: 20 }).catch((err) => {
          console.warn('Events failed:', err);
          errors.recentEvents = 'Failed to load events';
          return { events: [] };
        }),
      ]);

      if (cancelled) return;

      // Update state with results
      if (results[0].status === 'fulfilled') setHealthTrend(results[0].value);
      if (results[1].status === 'fulfilled') setActivityHeatmap(results[1].value);
      if (results[2].status === 'fulfilled') setStageHistory(results[2].value);
      if (results[3].status === 'fulfilled') setBenchmarks(results[3].value);
      if (results[4].status === 'fulfilled') setRecentEvents(results[4].value?.events || []);

      // Load entity cohorts for top 3 entities (by configured order)
      const entityDefs = useConfigStore.getState().config?.entityDefinitions;
      const allEntityKeys = Object.keys(accountData.entities || {});
      const entities = getOrderedEntityKeys(entityDefs, allEntityKeys).slice(0, 3);
      if (entities.length > 0) {
        try {
          const cohortsPromises = entities.map((entity) =>
            api.getEntityCohorts(accountId, entity).catch(() => null)
          );
          const cohortsData = await Promise.all(cohortsPromises);
          if (cancelled) return;

          const cohortsMap: Record<string, any> = {};
          cohortsData.forEach((data, index) => {
            if (data) cohortsMap[entities[index]] = data;
          });
          setEntityCohorts(cohortsMap);
        } catch (err) {
          console.warn('Entity cohorts failed:', err);
          errors.entityCohorts = 'Failed to load entity cohorts';
        }
      }

      if (cancelled) return;
      setSectionErrors(errors);
      setSectionsLoading(false);
    };

    loadAccount();
    return () => { cancelled = true; };
  }, [accountId]);

  const getCompanyName = () => {
    return account?.metadata?.companyName || accountId || 'Unknown Account';
  };

  // Backend health-trend contract: 'improving' | 'declining' | 'stable'
  const getTrendIcon = (trend: string) => {
    if (trend === 'improving') return <TrendingUp sx={{ fontSize: 16 }} />;
    if (trend === 'declining') return <TrendingDown sx={{ fontSize: 16 }} />;
    return <Remove sx={{ fontSize: 16 }} />;
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'improving') return 'success.main';
    if (trend === 'declining') return 'error.main';
    return 'text.secondary';
  };

  /** 'YYYY-MM-DD' strings are UTC dates; parsing them with new Date() shifts a day in western time zones. */
  const parseChartDate = (value: string): Date =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00Z`) : new Date(value);

  const getHealthScoreColor = (score: number) => {
    if (score >= 75) return 'success';
    if (score >= 50) return 'warning';
    if (score >= 25) return 'error';
    return 'error';
  };

  const getHealthScoreLabel = (score: number) => {
    if (score >= 75) return 'Healthy';
    if (score >= 50) return 'Moderate';
    if (score >= 25) return 'At Risk';
    return 'Critical';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const formatEventType = (eventType: string) => {
    return eventType
      .split('.')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const calculateEntityVelocity = (entity: string) => {
    if (!account?.entities?.[entity]) return 0;

    const last7Days = account.entities[entity].last7Days || 0;
    return (last7Days / 7).toFixed(1);
  };

  if (accountLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (accountError || !account) {
    return (
      <Box>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">{accountError || 'Account not found'}</Alert>
      </Box>
    );
  }

  // Features adopted = enabled features / (configured feature definitions ∪ features
  // the account has touched). Using only the account's own map made the
  // denominator 0 for accounts that never used a feature.
  const accountFeatures = account.features || {};
  const findAccountFeature = (name: string) => {
    if (name in accountFeatures) return accountFeatures[name];
    const lower = name.toLowerCase();
    const key = Object.keys(accountFeatures).find((k) => k.toLowerCase() === lower);
    return key ? accountFeatures[key] : undefined;
  };
  const featureNames = Array.from(new Set([
    ...Object.keys(featureDefinitions || {}),
    ...Object.keys(accountFeatures).filter(
      (k) => !Object.keys(featureDefinitions || {}).some((d) => d.toLowerCase() === k.toLowerCase()),
    ),
  ]));
  const booleanFeatures = featureNames.map(
    (name) => [name, Boolean(findAccountFeature(name)?.enabled)] as [string, boolean],
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/accounts')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h4" fontWeight={600}>
            {getCompanyName()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {account.metadata?.ownerEmail || accountId}
          </Typography>
        </Box>
        <Chip
          label={`${getHealthScoreLabel(account.healthScore)} (${account.healthScore})`}
          color={getHealthScoreColor(account.healthScore)}
          sx={{ fontWeight: 600, fontSize: 16, py: 2, px: 3 }}
        />
      </Box>

      <Stack spacing={3}>
        {/* 1. Top Row: Subscription, Lifecycle Stage, Features Adopted */}
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Subscription Info */}
          <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Subscription
                </Typography>
                {account.subscription ? (
                  <>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {account.subscription.plan || 'Unknown Plan'}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={account.subscription.status || 'Unknown'}
                        color={account.subscription.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      {account.subscription.mrr != null && account.subscription.mrr > 0 && (
                        <Chip label={`$${account.subscription.mrr}/${account.subscription.interval || 'mo'}`} size="small" />
                      )}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No subscription
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Stage Info */}
          <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Lifecycle Stage
                </Typography>
                <Typography variant="h5" fontWeight={600} gutterBottom textTransform="capitalize">
                  {account.onboardingStage?.replace('_', ' ') || 'Signup'}
                </Typography>
                {account.metadata?.clientSince && (
                  <Typography variant="body2" color="text.secondary">
                    Customer since {formatDate(account.metadata.clientSince)}
                  </Typography>
                )}
                {account.archived && (
                  <Chip label="Archived · not billed" size="small" sx={{ mb: 0.5 }} />
                )}
                {account.onboardingStage === 'churned' && (
                  <Typography variant="body2" color="error.main" mt={0.5}>
                    {({
                      subscription_cancelled: 'Subscription cancelled',
                      never_activated: 'Never activated (no data, no subscription)',
                      inactive: 'Inactive for too long',
                      rule: 'Matched churn conditions',
                    } as Record<string, string>)[account.metadata?.churnReason || 'rule'] || 'Churned'}
                    {account.metadata?.churnedAt ? ` · ${formatDate(account.metadata.churnedAt)}` : ''}
                  </Typography>
                )}
                {account.onboardingStage !== 'churned' && account.metadata?.reactivatedAt && (
                  <Typography variant="body2" color="success.main" mt={0.5}>
                    Reactivated {formatDate(account.metadata.reactivatedAt)}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Features Adopted */}
          <Box sx={{ flex: '1 1 calc(33.333% - 16px)', minWidth: 250 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Features Adopted
                </Typography>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {booleanFeatures.filter(([_, val]) => val).length} / {booleanFeatures.length}
                </Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                  {booleanFeatures.slice(0, 3).map(([name, enabled]) => (
                    <Tooltip key={name} title={name}>
                      <Chip
                        icon={enabled ? <CheckCircle /> : <Cancel />}
                        label={name.length > 12 ? `${name.slice(0, 12)}...` : name}
                        size="small"
                        color={enabled ? 'success' : 'default'}
                        variant={enabled ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* 2. Entity Velocity */}
        {Object.keys(account.entities || {}).length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Speed />
              <Typography variant="h6" fontWeight={600}>
                Entity Velocity (per day, last 7 days)
              </Typography>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              {getOrderedEntityKeys(config?.entityDefinitions, Object.keys(account.entities || {}))
                .filter((entity) => account.entities?.[entity])
                .map((entity) => [entity, account.entities![entity]] as [string, any])
                .map(([entity, data]) => (
                <Box key={entity} sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 200 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" textTransform="capitalize" gutterBottom>
                        {entity}
                      </Typography>
                      <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography variant="h4" fontWeight={600}>
                          {calculateEntityVelocity(entity)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          / day
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Total: {data.count} • Last 7d: {data.last7Days || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* 3. Risk Assessment */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Risk Assessment
          </Typography>

          {sectionsLoading && (!healthTrend || !activityHeatmap) ? (
            <Box>
              <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ mb: 1.5, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Box>
          ) : (
            <RiskIndicators
              account={account}
              healthTrend={healthTrend}
              activityHeatmap={activityHeatmap}
            />
          )}
        </Paper>

        {/* 4. Stage Progression (50%) and Key Milestones (50%) */}
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Stage Progression - Half Width */}
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 400 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Stage Progression
              </Typography>

              {sectionsLoading && !stageHistory ? (
                <Box>
                  <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 1 }} />
                </Box>
              ) : sectionErrors.stageHistory ? (
                <Alert severity="warning">{sectionErrors.stageHistory}</Alert>
              ) : stageHistory && stageHistory.transitions && stageHistory.transitions.length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Journey through {stageHistory.totalStageChanges || 0} stage{stageHistory.totalStageChanges !== 1 ? 's' : ''}
                  </Typography>
                  <StageProgressionTimeline
                    transitions={stageHistory.transitions}
                    currentStage={stageHistory.currentStage}
                  />
                </>
              ) : (
                <Alert severity="info">No stage progression history available</Alert>
              )}
            </Paper>
          </Box>

          {/* Key Milestones - Half Width */}
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 400 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Key Milestones
              </Typography>

              {sectionsLoading && !stageHistory ? (
                <Box>
                  <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
                </Box>
              ) : (
                <KeyMilestones account={account} stageHistory={stageHistory} />
              )}
            </Paper>
          </Box>
        </Box>

        {/* 5. Feature Adoption Timeline (50%) and Activity Heatmap (50%) */}
        <Box display="flex" gap={3} flexWrap="wrap">
          {/* Feature Adoption Timeline - Half Width */}
          {booleanFeatures.length > 0 && (
            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 400 }}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Feature Adoption Timeline
                </Typography>
                <FeatureAdoptionTimeline features={account.features || {}} />
              </Paper>
            </Box>
          )}

          {/* Activity Heatmap - Half Width */}
          <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 400 }}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Activity Heatmap (90 Days)
              </Typography>

              {sectionsLoading && !activityHeatmap ? (
                <Box>
                  <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </Box>
              ) : sectionErrors.activityHeatmap ? (
                <Alert severity="warning">{sectionErrors.activityHeatmap}</Alert>
              ) : activityHeatmap ? (
                <>
                  {/* Summary Stats */}
                  <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Events
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {activityHeatmap.summary.totalEvents}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Active Days
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {activityHeatmap.summary.activeDays} / {activityHeatmap.days}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current Streak
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {activityHeatmap.summary.currentStreak} days
                      </Typography>
                    </Box>
                  </Box>

                  {/* Heatmap */}
                  <ActivityHeatmap
                    data={activityHeatmap.heatmapData}
                    maxCount={activityHeatmap.summary.maxEventsInDay}
                  />
                </>
              ) : null}
            </Paper>
          </Box>
        </Box>

        {/* 6. Health Score Trend - Full Width */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Health Score Trend (90 Days)
          </Typography>

          {sectionsLoading && !healthTrend ? (
            <Box>
              <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
            </Box>
          ) : sectionErrors.healthTrend ? (
            <Alert severity="warning">{sectionErrors.healthTrend}</Alert>
          ) : healthTrend?.dataPoints?.length > 0 ? (
            <>
              <Box display="flex" gap={3} mb={3} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {healthTrend.summary.current}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Change
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="h5" fontWeight={600} color={getTrendColor(healthTrend.summary.trend)}>
                      {healthTrend.summary.change > 0 ? '+' : ''}
                      {healthTrend.summary.change}
                    </Typography>
                    {getTrendIcon(healthTrend.summary.trend)}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Average
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {healthTrend.summary.average}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Min / Max
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>
                    {healthTrend.summary.min} / {healthTrend.summary.max}
                  </Typography>
                </Box>
              </Box>

              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={healthTrend.dataPoints}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                      const date = parseChartDate(String(value));
                      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
                    }}
                  />
                  <YAxis domain={[0, 100]} />
                  <ChartTooltip
                    labelFormatter={(value) => parseChartDate(String(value)).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="healthScore" stroke="#FFB800" strokeWidth={2} name="Health Score" />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <Alert severity="info">No health score data available for the last 90 days</Alert>
          )}
        </Paper>

        {/* 7. Entity Activity Trends - Full Width */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Entity Activity Trends
          </Typography>

          {sectionsLoading && Object.keys(entityCohorts).length === 0 ? (
            <Box display="flex" flexWrap="wrap" gap={3}>
              <Skeleton variant="rectangular" width="calc(33% - 16px)" height={250} sx={{ borderRadius: 1, minWidth: 300 }} />
              <Skeleton variant="rectangular" width="calc(33% - 16px)" height={250} sx={{ borderRadius: 1, minWidth: 300 }} />
              <Skeleton variant="rectangular" width="calc(33% - 16px)" height={250} sx={{ borderRadius: 1, minWidth: 300 }} />
            </Box>
          ) : sectionErrors.entityCohorts ? (
            <Alert severity="warning">{sectionErrors.entityCohorts}</Alert>
          ) : Object.keys(entityCohorts).length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={3}>
              {getOrderedEntityKeys(config?.entityDefinitions, Object.keys(entityCohorts))
                .filter((entity) => entityCohorts[entity])
                .map((entity) => [entity, entityCohorts[entity]] as [string, any])
                .map(([entity, data]) => (
                <Box key={entity} sx={{ flex: '1 1 300px', minWidth: 300 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2} textTransform="capitalize">
                      {entity}s Created
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Week comparison */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Weekly Trends
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2">This Week</Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {data.cohorts.thisWeek.count}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2">Last Week</Typography>
                          <Typography variant="body2">{data.cohorts.lastWeek.count}</Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">2 Weeks Ago</Typography>
                          <Typography variant="body2">{data.cohorts.twoWeeksAgo.count}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                          <Typography variant="caption" color={getTrendColor(data.changes.weekOverWeek.trend)}>
                            {data.changes.weekOverWeek.value > 0 ? '+' : ''}
                            {data.changes.weekOverWeek.value} ({data.changes.weekOverWeek.percentage.toFixed(1)}%)
                          </Typography>
                          {getTrendIcon(data.changes.weekOverWeek.trend)}
                        </Box>
                      </Box>

                      <Divider />

                      {/* Month comparison */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          Monthly Trends
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2">This Month</Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {data.cohorts.thisMonth.count}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Last Month</Typography>
                          <Typography variant="body2">{data.cohorts.lastMonth.count}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                          <Typography variant="caption" color={getTrendColor(data.changes.monthOverMonth.trend)}>
                            {data.changes.monthOverMonth.value > 0 ? '+' : ''}
                            {data.changes.monthOverMonth.value} ({data.changes.monthOverMonth.percentage.toFixed(1)}%)
                          </Typography>
                          {getTrendIcon(data.changes.monthOverMonth.trend)}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          ) : (
            <Alert severity="info">No entity cohort data available</Alert>
          )}
        </Paper>

        {/* 8. Performance Comparison - Full Width */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Performance Comparison
          </Typography>

          {sectionsLoading && !benchmarks ? (
            <Box>
              <Skeleton variant="rectangular" height={180} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </Box>
          ) : sectionErrors.benchmarks ? (
            <Alert severity="warning">{sectionErrors.benchmarks}</Alert>
          ) : benchmarks && benchmarks.benchmarks ? (
            <ComparisonBenchmarks data={benchmarks} />
          ) : (
            <Alert severity="info">No benchmark data available</Alert>
          )}
        </Paper>

        {/* 9. Recent Activity - Full Width */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <EventNote />
            <Typography variant="h6" fontWeight={600}>
              Recent Events
            </Typography>
          </Box>

          {sectionsLoading && recentEvents.length === 0 ? (
            <Box>
              <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Box>
          ) : sectionErrors.recentEvents ? (
            <Alert severity="warning">{sectionErrors.recentEvents}</Alert>
          ) : recentEvents.length > 0 ? (
            <List>
              {recentEvents.map((event, index) => (
                <React.Fragment key={event.eventId}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight={500}>
                            {formatEventType(event.eventType)}
                          </Typography>
                          <Chip label={event.eventType} size="small" variant="outlined" />
                        </Box>
                      }
                      secondary={
                        <Box mt={0.5}>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <CalendarToday sx={{ fontSize: 14 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(event.timestamp)}
                            </Typography>
                          </Box>
                          {event.data && Object.keys(event.data).length > 0 && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              {Object.entries(event.data)
                                .slice(0, 3)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(' • ')}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < recentEvents.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="info">No recent activity</Alert>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};
